import React from 'react';
import type { ActivityViewProps } from '../../core/types/activity.types';
import type {
  SourceAnalysisPayload,
  SourceAnalysisRuntime,
} from '../../core/types/payloads/source_analysis.types';

function NotebookLines() {
  return (
    <div
      className="pointer-events-none absolute inset-0"
      style={{
        backgroundImage:
          'repeating-linear-gradient(transparent, transparent 27px, rgb(203 213 225 / 0.55) 28px)',
        backgroundPositionY: '8px',
      }}
    />
  );
}

export function SourceAnalysisBlock({
  payload,
  readOnly = false,
  responses = {},
  onDraft,
  onSubmit,
  submitted = false,
}: {
  payload: SourceAnalysisPayload;
  readOnly?: boolean;
  responses?: Record<number, string>;
  onDraft?: (questionIndex: number, text: string) => void;
  onSubmit?: () => void;
  submitted?: boolean;
}) {
  return (
    <div className="space-y-5">
      {payload.title ? <h3 className="text-sm font-semibold text-slate-800">{payload.title}</h3> : null}

      <figure className="rounded-r-md border-l-4 border-slate-800 bg-stone-50 px-4 py-4">
        <blockquote className="font-serif text-base italic leading-relaxed text-slate-800">
          {payload.source.quote.trim() ? `“${payload.source.quote.replace(/^[«"“]+|[»"”]+$/g, '')}”` : '—'}
        </blockquote>
        <figcaption className="mt-3 text-right text-xs font-bold text-slate-700">
          {payload.source.reference}
        </figcaption>
      </figure>

      <div className="space-y-8">
        {payload.questions.map((question, index) => (
          <div key={index} className="mb-12 space-y-3">
            <p className="text-sm font-medium leading-snug text-slate-800">
              {index + 1}. {question}
            </p>
            {readOnly ? (
              <p className="min-h-[5rem] whitespace-pre-wrap border-b border-slate-200 pb-2 text-sm text-slate-700">
                {responses[index] || ' '}
              </p>
            ) : (
              <div className="relative min-h-[7rem] rounded-md border border-slate-200 bg-white">
                <NotebookLines />
                <textarea
                  className="relative z-[1] min-h-[7rem] w-full resize-y border-0 bg-transparent px-3 py-2 text-sm leading-7 text-slate-800 outline-none"
                  disabled={submitted}
                  value={responses[index] ?? ''}
                  onChange={(e) => onDraft?.(index, e.target.value)}
                  placeholder="Escribe tu respuesta…"
                />
              </div>
            )}
          </div>
        ))}
      </div>

      {!readOnly && !submitted && onSubmit ? (
        <button type="button" className="sala-btn-hero text-xs" onClick={onSubmit}>
          Enviar respuestas
        </button>
      ) : null}
    </div>
  );
}

function dispatchSubmit(dispatch: ActivityViewProps['dispatch'], action: string, data?: unknown) {
  void dispatch({ type: action, payload: data });
}

export function SourceAnalysisStudentView({
  payload,
  runtime,
  dispatch,
  participantId,
}: ActivityViewProps<'source_analysis'>) {
  const rt = runtime as SourceAnalysisRuntime;
  const responses = participantId ? rt.responses[participantId] ?? {} : {};
  const submitted = participantId ? Boolean(rt.finalized[participantId]) : false;

  return (
    <SourceAnalysisBlock
      payload={payload}
      responses={responses}
      submitted={submitted}
      onDraft={(questionIndex, text) =>
        dispatchSubmit(dispatch, 'DRAFT', { questionIndex, text })
      }
      onSubmit={() => dispatchSubmit(dispatch, 'SUBMIT')}
    />
  );
}

export function SourceAnalysisHostView({ payload, runtime }: ActivityViewProps<'source_analysis'>) {
  const rt = runtime as SourceAnalysisRuntime;
  return (
    <div className="eval-block eval-block--host space-y-3">
      <SourceAnalysisBlock payload={payload} readOnly />
      <p className="text-xs text-slate-500">Enviados: {rt.aggregates.submitted}</p>
    </div>
  );
}

export const SourceAnalysisProjectorView = SourceAnalysisHostView;
